import { useLazyQuery, useMutation, useQuery } from '@provision/graphql-client';
import {
  GET_CONVERSATION_LIVE_TRANSCRIPTION,
  GET_SIDEKICK_CHAT_SESSIONS,
  GET_SIDEKICK_RUN_RESULT,
  GET_SIDEKICK_RUNS,
  GET_SIDEKICK_SESSION_RUNS,
  GET_SIDEKICK_SUGGESTED_QUESTIONS,
  GetConversationLiveTranscriptionResponse,
  GetConversationLiveTranscriptionVariables,
  GetSidekickChatSessionsVariables,
  GetSidekickRunResultVariables,
  GetSidekickSessionRunsVariables,
  GetSidekickSuggestedQuestionsVariables,
  SidekickChatSession,
  SidekickRun,
  SidekickRunResult,
  SidekickSessionRun,
  SidekickSuggestedQuestions,
  START_SIDEKICK_RUN,
  StartSidekickRunVariables,
} from './sidekick.operations';

export const useSidekickRuns = (conversationId?: string, partnerId?: number | null) => {
  const hasConversation = Boolean(conversationId);
  const hasPartner = partnerId !== null && partnerId !== undefined;
  const shouldQuery = hasConversation && hasPartner;

  return useQuery<{ getSidekickRuns: SidekickRun[] }, GetSidekickChatSessionsVariables>(
    GET_SIDEKICK_RUNS,
    {
      variables: {
        params: {
          conversationId: conversationId!,
          partnerId: partnerId!,
        },
      },
      skip: !shouldQuery,
      fetchPolicy: 'cache-and-network',
    },
  );
};

export const useStartSidekickRun = () =>
  useMutation<{ startSidekickRun: SidekickRun }, StartSidekickRunVariables>(
    START_SIDEKICK_RUN,
  );

export const useSidekickRunResult = () =>
  useLazyQuery<{ getSidekickRunResult: SidekickRunResult }, GetSidekickRunResultVariables>(
    GET_SIDEKICK_RUN_RESULT,
    {
      fetchPolicy: 'no-cache',
    },
  );

export const useSidekickSuggestedQuestions = (
  conversationId?: string,
  partnerId?: number | null,
  limit = 5,
) => {
  const hasConversation = Boolean(conversationId);
  const hasPartner = partnerId !== null && partnerId !== undefined;
  const shouldQuery = hasConversation && hasPartner;

  return useQuery<
    { getSidekickSuggestedQuestions: SidekickSuggestedQuestions },
    GetSidekickSuggestedQuestionsVariables
  >(GET_SIDEKICK_SUGGESTED_QUESTIONS, {
    variables: {
      params: {
        conversationId: conversationId!,
        partnerId: partnerId!,
        limit,
      },
    },
    skip: !shouldQuery,
    fetchPolicy: 'cache-and-network',
  });
};

export const useSidekickChatSessions = (
  conversationId?: string,
  partnerId?: number | null,
) => {
  const hasConversation = Boolean(conversationId);
  const hasPartner = partnerId !== null && partnerId !== undefined;
  const shouldQuery = hasConversation && hasPartner;

  return useQuery<
    { getSidekickChatSessions: SidekickChatSession[] },
    GetSidekickChatSessionsVariables
  >(GET_SIDEKICK_CHAT_SESSIONS, {
    variables: {
      params: {
        conversationId: conversationId!,
        partnerId: partnerId!,
      },
    },
    skip: !shouldQuery,
    fetchPolicy: 'cache-and-network',
  });
};

export const useSidekickSessionRuns = () =>
  useLazyQuery<
    { getSidekickSessionRuns: SidekickSessionRun[] },
    GetSidekickSessionRunsVariables
  >(GET_SIDEKICK_SESSION_RUNS, { fetchPolicy: 'no-cache' });

export const useConversationLiveTranscription = () =>
  useLazyQuery<
    { getConversationLiveTranscription: GetConversationLiveTranscriptionResponse | null },
    GetConversationLiveTranscriptionVariables
  >(GET_CONVERSATION_LIVE_TRANSCRIPTION, { fetchPolicy: 'no-cache' });
